import { lightPhase, type LightPhase } from './sun.js';

/**
 * What the world looks like for a given solar elevation (spec §6.10). Pure, so the client's
 * renderer, the editor and tests all agree; colours are 0xRRGGBB.
 */
export interface Lighting {
  readonly elevation: number;
  readonly phase: LightPhase;
  /** Colour laid over outdoor tiles. */
  readonly outdoorColor: number;
  /** Opacity of that colour, 0 (day) to about 0.7 (night). */
  readonly outdoorAlpha: number;
  /** Indoors stays lit by the office lights and only gets a subtle tint. */
  readonly indoorColor: number;
  readonly indoorAlpha: number;
  /** Colour seen through windows. */
  readonly skyColor: number;
  /** Stars are visible in windows and outdoors (0..1). */
  readonly stars: number;
  /** Lamps are switched on (the sun is below the horizon). */
  readonly lampsOn: boolean;
}

interface Anchor {
  readonly elevation: number;
  readonly tint: number;
  readonly alpha: number;
  readonly sky: number;
  readonly stars: number;
}

/** Key points of the spec §6.10 table; values in between are interpolated. Highest first. */
const ANCHORS: readonly Anchor[] = [
  { elevation: 6, tint: 0xffffff, alpha: 0, sky: 0x9fd3ff, stars: 0 }, // day: neutral
  { elevation: 0, tint: 0xff9a3c, alpha: 0.22, sky: 0xffb36b, stars: 0 }, // golden hour
  { elevation: -6, tint: 0x6a5acd, alpha: 0.42, sky: 0x5b4f9e, stars: 0.2 }, // civil twilight
  { elevation: -12, tint: 0x1f2a6b, alpha: 0.58, sky: 0x1b2457, stars: 0.7 }, // nautical
  { elevation: -18, tint: 0x0b1030, alpha: 0.68, sky: 0x0b1030, stars: 1 }, // night
];

const NEUTRAL: Anchor = { elevation: 90, tint: 0xffffff, alpha: 0, sky: 0x9fd3ff, stars: 0 };

/** Indoor tint strength relative to outdoors. */
const INDOOR_SHARE = 0.3;
/** Lamps switch on at sunset. */
const LAMPS_ON_BELOW = 0;

/**
 * Lighting for a solar elevation in degrees. `intensity` (0..1, default 1) scales all darkness:
 * the accessibility "darkness intensity" setting.
 */
export function lightingFor(elevation: number, intensity = 1): Lighting {
  const scale = Math.min(Math.max(intensity, 0), 1);
  const [upper, lower, t] = bracket(elevation);
  const outdoorAlpha = lerp(upper.alpha, lower.alpha, t) * scale;
  const outdoorColor = lerpColor(upper.tint, lower.tint, t);
  return {
    elevation,
    phase: lightPhase(elevation),
    outdoorColor,
    outdoorAlpha,
    indoorColor: outdoorColor,
    indoorAlpha: outdoorAlpha * INDOOR_SHARE,
    skyColor: lerpColor(upper.sky, lower.sky, t),
    stars: lerp(upper.stars, lower.stars, t),
    lampsOn: elevation < LAMPS_ON_BELOW,
  };
}

/** The two anchors around `elevation` and the position between them (0 = upper, 1 = lower). */
function bracket(elevation: number): [Anchor, Anchor, number] {
  let upper: Anchor | undefined;
  for (const lower of ANCHORS) {
    if (elevation >= lower.elevation) {
      if (!upper) return [lower, lower, 0]; // above the highest anchor
      return [upper, lower, (upper.elevation - elevation) / (upper.elevation - lower.elevation)];
    }
    upper = lower;
  }
  // Below the lowest anchor: full night.
  return upper ? [upper, upper, 0] : [NEUTRAL, NEUTRAL, 0];
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Interpolates two 0xRRGGBB colours channel by channel. */
export function lerpColor(a: number, b: number, t: number): number {
  const channel = (shift: number) =>
    Math.round(lerp((a >> shift) & 0xff, (b >> shift) & 0xff, t)) << shift;
  return channel(16) | channel(8) | channel(0);
}

/**
 * Keeps the client's idea of "now" in step with the server (spec §6.10: lighting follows
 * server-synced time, so a wrong local clock does not change the sky).
 */
export class ServerClock {
  private offsetMs = 0;
  private synced = false;

  constructor(private readonly localNow: () => number = Date.now) {}

  /** Records a server timestamp received now. Ignores 0 (not yet set by the server). */
  sync(serverTimeMs: number): void {
    if (!Number.isFinite(serverTimeMs) || serverTimeMs <= 0) return;
    this.offsetMs = serverTimeMs - this.localNow();
    this.synced = true;
  }

  get isSynced(): boolean {
    return this.synced;
  }

  now(): Date {
    return new Date(this.localNow() + this.offsetMs);
  }
}
